/**
 * Container component for an individual location to update.
 */
import { useState } from 'react';
import type { Location } from '../../../types/Location';
import { deleteLocation, putLocationSettings, putLocationStatuses } from '../../../utils/api';
import { useToast } from '../../../hooks/useToast';
import AccordionBasic from '../../accordions/AccordionBasic';
import ChangeDisplayNameSection from './sections/ChangeDisplayNameSection';
import { type HoursOfOperation } from '@/types/HoursOfOperation';
import ChangeOpenAndCloseSection from './sections/ChangeOpenAndCloseSection';
import ChangeNotificationStatusSection from './sections/ChangeNotificationStatusSection';
import StandardButton from '@/components/buttons/StandardButton';
import { LocationStatus } from '@/types/LocationStatus';
import SectionTitle from './sections/SectionTitle';
import { StatusRequestObject } from '../account/types';

export default function LocationContainer({
  location,
  statuses,
}: {
  location: Location;
  statuses: LocationStatus[];
}): JSX.Element {
  const [displayName, setDisplayName] = useState(location.displayName);
  const [hoursOfOperation, setHoursOfOperation] = useState<HoursOfOperation>(
    location.openOrCloseData ?? { open: '', close: '' }
  );
  const [updates, setUpdates] = useState<Map<string, StatusRequestObject>>(new Map<string, StatusRequestObject>());
  const { setToast } = useToast();

  // Handle changing the display name input.
  function handleDisplayNameInputChange(
    e: React.ChangeEvent<HTMLInputElement>
  ): void {
    setDisplayName(e.target.value);
  }

  async function handleLocationDelete(
    locationId: string,
    franchisId: string
  ): Promise<void> {
    await deleteLocation(franchisId, locationId);
  }

  function handleHoursOfOperationTime(dateStr: string, isOpen: boolean): void {
    setHoursOfOperation((prev) => ({
      ...prev,
      [isOpen ? 'open' : 'close']: dateStr,
    }));
  }

  function handleStatusUpdate(requestObj: StatusRequestObject): void {
    setUpdates(prev => {
      return {
        ...prev,
        [requestObj.statusType]: requestObj,
      };
    });
  }

  async function handleSaveLocations(): Promise<void> {
    const res = await putLocationSettings(
      location.franchiseId,
      location.id,
      displayName,
      hoursOfOperation
    );
    let statusRes = true;
    if (updates) {
      // Set these with another api call
      statusRes = await putLocationStatuses(
        location.franchiseId,
        location.id,
        updates
      )
    }

    if (res && statusRes) {
      setToast({
        show: true,
        message: 'Location settings updated',
        type: 'success',
      });
    } else if (res) {
      // Edge case where settings update but statuses do not
      setToast({
        show: true,
        message: 'Error updating location status thresholds',
        type: 'error',
      });
    }
    else {
      // Either settings error or all errors
      setToast({
        show: true,
        message: 'Error updating location settings',
        type: 'error',
      });
    }
  }

  function handleSave(): void {
    handleSaveLocations().catch((error) => {
      console.error('Error saving location settings', error);
      setToast({
        show: true,
        message: 'Error saving location settings',
        type: 'error',
      });
    });
  }

  function getTimeAsNumberOfMinutes(time: string) {
    const timeParts = time.split(':');
    return (parseInt(timeParts[0], 10) * 60) + parseInt(timeParts[1], 10);
  }

  async function showConfirmationDialog(): Promise<boolean> {
    if (getTimeAsNumberOfMinutes(hoursOfOperation.open) <= getTimeAsNumberOfMinutes(hoursOfOperation.close)) {
      return true;
    }
    return await new Promise((resolve) => {
      const isConfirmed = window.confirm('The closing time is before the opening time. Is the close time on the following day?');
      resolve(isConfirmed);
    });
  }

  return (
    <AccordionBasic title={location.displayName} dataCy="location-accordion">
      <div className="flex flex-col gap-4">
        {/* Change Display Name */}
        <ChangeDisplayNameSection
          displayName={displayName}
          locationId={location.id}
          franchiseId={location.franchiseId}
          handleInputChange={handleDisplayNameInputChange}
          handleLocationDelete={handleLocationDelete}
        />

        {statuses.length > 0 && (
          <section>
            <SectionTitle title="Change Notification Thresholds" />
            {statuses.map((s, i) => (<ChangeNotificationStatusSection
              key={location.id + i}
              status={s.statusType}
              threshold={s.threshold}
              cooldown={s.notificationThreshold}
              handleStatusUpdate={handleStatusUpdate}
            />))}
          </section>
        )}

        <ChangeOpenAndCloseSection
          hoursOfOperation={hoursOfOperation}
          handleHoursOfOperationTime={handleHoursOfOperationTime}
        />

        <div className="flex flex-wrap gap-8 w-full">
          <StandardButton
            onClick={handleSave}
            dataCy="update-day-part-button"
            confirmDialog={showConfirmationDialog}
          >
            Save Updates
          </StandardButton>
        </div>
        <h3>Location ID: {location.id}</h3>
      </div>
    </AccordionBasic>
  );
}
